import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Download, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QRMenuManagement = ({ token }) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTables(); }, []);

  const fetchTables = async () => {
    try {
      const res = await axios.get(`${API}/admin/tables`, { headers: { Authorization: `Bearer ${token}` } });
      setTables(res.data);
    } catch { toast.error('Masa bilgileri yüklenemedi'); } finally { setLoading(false); }
  };

  const downloadQR = (tableNumber) => {
    // Open in new tab - Content-Disposition: attachment forces download
    const win = window.open(`${API}/qr/generate/${tableNumber}`, '_blank');
    if (!win) {
      // Popup blocked - fallback to iframe download
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${API}/qr/generate/${tableNumber}`;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 10000);
    }
  };

  const downloadAllQR = () => {
    const win = window.open(`${API}/qr/generate-all`, '_blank');
    if (!win) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${API}/qr/generate-all`;
      document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 10000);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link kopyalandı!');
  };

  if (loading) return <div className="text-center py-8">Yükleniyor...</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-playfair text-2xl font-semibold text-charcoal">QR Menü Yönetimi</h2>
            <p className="text-charcoal/60 mt-1">Toplam {tables.length} masa için QR kod</p>
          </div>
          <Button onClick={downloadAllQR} className="bg-basil text-white hover:bg-basil/90">
            <Download className="w-4 h-4 mr-2" />
            Tümünü İndir (ZIP)
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-terracotta/10 border border-terracotta/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <QrCode className="w-5 h-5 text-terracotta mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-charcoal">QR Kod Kullanımı</p>
            <p className="text-charcoal/70 text-sm">
              Her masa için QR kodu indirip masalara yerleştirin. Müşteriler QR kodu tarayarak menüye ulaşabilir ve sipariş verebilir.
            </p>
          </div>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {tables.map((table) => (
          <div key={table.table_number} className="bg-white rounded-xl p-4 shadow-sm border border-stone-100">
            <div className="text-center">
              {/* QR Preview */}
              <div className="w-32 h-32 mx-auto mb-3 bg-stone-50 rounded-lg flex items-center justify-center">
                <img 
                  src={`${API}/qr/preview/${table.table_number}`} 
                  alt={`Masa ${table.table_number} QR`}
                  className="w-28 h-28"
                />
              </div>
              
              {/* Table Info */}
              <h3 className="font-playfair text-xl font-bold text-charcoal mb-1">Masa {table.table_number}</h3>
              
              {/* Stats */}
              <div className="flex justify-center gap-4 text-sm mb-3">
                <div className="text-center">
                  <p className="text-charcoal/60">Aktif</p>
                  <p className={`font-bold ${table.active_orders > 0 ? 'text-terracotta' : 'text-charcoal'}`}>
                    {table.active_orders}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-charcoal/60">Bugün</p>
                  <p className="font-bold text-basil">{table.today_orders}</p>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => downloadQR(table.table_number)}
                >
                  <Download className="w-4 h-4 mr-1" />
                  İndir
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => copyLink(table.qr_url)}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default QRMenuManagement;
